// Browser storage that never throws (FE-07). Pure: no React, no client.
//
// Reaching for localStorage or sessionStorage throws a SecurityError when the
// browser blocks site data, and setItem throws when the store is full. Every
// call here returns a safe value instead, so a blocked store never breaks a
// screen: it only loses what would have been kept.
//
// Sensitive drafts and caches (SENSITIVE_BROWSER_DATA) are browser copies that
// hold player, case or review content. Each keeps the time it was last changed
// ({ at, ... }) and is dropped once it is older than its maximum age (24 hours
// unless listed otherwise). All of them are removed when the session ends
// (clearSensitiveBrowserData: sign-out, a forced sign-out, or an app that
// opens with nobody signed in). Preferences (theme, columns, pins, remembered
// tabs and periods) are not listed and stay.

export const SENSITIVE_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const STORAGE_KINDS = Object.freeze(['localStorage', 'sessionStorage']);

// The store, or null when there is none or the browser refuses it.
export function browserStorage(kind = 'localStorage') {
  if (!STORAGE_KINDS.includes(kind)) return null;
  try {
    return globalThis[kind] || null;
  } catch {
    return null;
  }
}

// The stored text, or null when nothing is stored or the store cannot be read.
export function readStoredItem(kind, key) {
  const storage = browserStorage(kind);
  if (!storage) return null;
  try {
    const value = storage.getItem(key);
    return value === undefined ? null : value;
  } catch {
    return null;
  }
}

// true when the store kept the value.
export function writeStoredItem(kind, key, value) {
  const storage = browserStorage(kind);
  if (!storage) return false;
  try {
    storage.setItem(key, String(value));
    return true;
  } catch {
    return false;
  }
}

// true when the store could be asked to forget the key.
export function removeStoredItem(kind, key) {
  const storage = browserStorage(kind);
  if (!storage) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

// Keys nothing reads or writes any more. A copy an earlier version left in
// this browser is removed (removeRetiredBrowserKeys).
export const RETIRED_BROWSER_KEYS = Object.freeze([
  // The detection settings kept in the browser before they were governed.
  'det_engine_v3',
  // The allowed invitation domains, written by earlier versions and never read.
  'riskTool_allowedDomains',
]);

export function removeRetiredBrowserKeys() {
  for (const key of RETIRED_BROWSER_KEYS) removeStoredItem('localStorage', key);
}

// Every browser copy that holds player, case or review content: where it is
// kept, the start of its key, and how long it lasts after its last change.
export const SENSITIVE_BROWSER_DATA = Object.freeze([
  Object.freeze({ id:'case-create-draft', kind:'localStorage', prefix:'cases:create-draft:', maxAgeMs:SENSITIVE_DRAFT_MAX_AGE_MS,
    holds:'Create case drafts: title, reason, description and player subjects (src/caseCreateDraft.js).' }),
  Object.freeze({ id:'player-investigation-tabs', kind:'sessionStorage', prefix:'player-investigation-tabs:', maxAgeMs:SENSITIVE_DRAFT_MAX_AGE_MS,
    holds:'Player Intelligence open player tabs: player IDs, risk bands, quick note drafts and each tab\'s view (src/PlayerIntelligencePages.js).' }),
  Object.freeze({ id:'player-investigation-recent', kind:'sessionStorage', prefix:'player-investigation-recent:', maxAgeMs:SENSITIVE_DRAFT_MAX_AGE_MS,
    holds:'Player Intelligence recent players: the player IDs opened last (src/PlayerIntelligencePages.js).' }),
  Object.freeze({ id:'player-layout-editor-context', kind:'sessionStorage', prefix:'pi-layout-editor-context', maxAgeMs:10 * 60 * 1000,
    holds:'The player and tab the layout editor previews, read once within 10 minutes (src/playerIntelligenceLayoutDraft.js).' }),
]);

export function sensitiveBrowserDataFor(kind, key) {
  const text = String(key ?? '');
  return SENSITIVE_BROWSER_DATA.find(entry => entry.kind === kind && text.startsWith(entry.prefix)) || null;
}

function storedKeys(storage) {
  const keys = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (typeof key === 'string') keys.push(key);
    }
  } catch {
    // A store that cannot be listed keeps nothing this code can find.
  }
  return keys;
}

// A save time counts while it is no older than maxAgeMs and not further ahead
// of this clock than that either (a clock set back keeps it for a while only).
export function savedTimeIsCurrent(at, maxAgeMs = SENSITIVE_DRAFT_MAX_AGE_MS, now = Date.now()) {
  const time = typeof at === 'number' || (typeof at === 'string' && at.trim()) ? Number(at) : NaN;
  return Number.isFinite(time) && time > 0 && now - time < maxAgeMs && time - now < maxAgeMs;
}

function savedTime(raw) {
  try {
    const value = JSON.parse(raw);
    return value && typeof value === 'object' && !Array.isArray(value) ? value.at : undefined;
  } catch {
    return undefined;
  }
}

// Removes every sensitive draft and cache in this browser (this tab's session
// store and the shared local store), whoever saved it. Returns the number of
// keys removed.
export function clearSensitiveBrowserData() {
  let removed = 0;
  for (const kind of STORAGE_KINDS) {
    const storage = browserStorage(kind);
    if (!storage) continue;
    for (const key of storedKeys(storage)) {
      if (sensitiveBrowserDataFor(kind, key) && removeStoredItem(kind, key)) removed += 1;
    }
  }
  return removed;
}

// Removes the sensitive drafts and caches whose last change is older than
// their maximum age, or that carry no readable save time. Returns the number
// of keys removed.
export function removeExpiredSensitiveBrowserData({ now = Date.now() } = {}) {
  let removed = 0;
  for (const kind of STORAGE_KINDS) {
    const storage = browserStorage(kind);
    if (!storage) continue;
    for (const key of storedKeys(storage)) {
      const entry = sensitiveBrowserDataFor(kind, key);
      if (!entry) continue;
      if (savedTimeIsCurrent(savedTime(readStoredItem(kind, key)), entry.maxAgeMs, now)) continue;
      if (removeStoredItem(kind, key)) removed += 1;
    }
  }
  return removed;
}

// A JSON value kept with the time of its last change: { at, value }. Reading
// gives { at, value }, or null when nothing is kept, the store cannot be read,
// or the copy is unreadable or older than maxAgeMs (that copy is removed).
export function readTimedItem(kind, key, { maxAgeMs = SENSITIVE_DRAFT_MAX_AGE_MS, now = Date.now() } = {}) {
  const raw = readStoredItem(kind, key);
  if (raw === null) return null;
  let stored = null;
  try { stored = JSON.parse(raw); } catch { stored = null; }
  if (!stored || typeof stored !== 'object' || Array.isArray(stored) || !('value' in stored) || !savedTimeIsCurrent(stored.at, maxAgeMs, now)) {
    removeStoredItem(kind, key);
    return null;
  }
  return { at:Number(stored.at), value:stored.value };
}

// Keeps value with the time of this change. A value that has not changed
// keeps the time it was saved, so its age counts from its last change.
// Returns that time, or null when the store did not keep it.
export function writeTimedItem(kind, key, value, { now = Date.now() } = {}) {
  const kept = value === undefined ? null : value;
  const serialized = JSON.stringify(kept);
  let at = now;
  const raw = readStoredItem(kind, key);
  if (raw !== null) {
    try {
      const earlier = JSON.parse(raw);
      const earlierAt = Number(earlier?.at);
      if (earlier && typeof earlier === 'object' && !Array.isArray(earlier) && JSON.stringify(earlier.value ?? null) === serialized
        && Number.isFinite(earlierAt) && earlierAt > 0 && earlierAt <= now) at = earlierAt;
    } catch {
      // An unreadable copy is replaced.
    }
  }
  return writeStoredItem(kind, key, JSON.stringify({ at, value:kept })) ? at : null;
}
